import {
  getWeaponById as getBaseWeaponById,
  pickRandomWeaponId,
  rarityColor,
  STARTER_WEAPON_IDS,
  WEAPON_CATEGORY,
} from '../data/weapons.data.js';

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const DEFAULT_SCALING = {
  common: { damage: 1.0, projectileSpeed: 1.0, spread: 1.0, cooldown: 1.0, magSize: 1.0, reserve: 1.0, reloadDuration: 1.0, projectileLife: 1.0, critChance: 1.0 },
  uncommon: { damage: 1.08, projectileSpeed: 1.04, spread: 0.95, cooldown: 0.97, magSize: 1.05, reserve: 1.07, reloadDuration: 0.96, projectileLife: 1.03, critChance: 1.08 },
  rare: { damage: 1.18, projectileSpeed: 1.1, spread: 0.88, cooldown: 0.93, magSize: 1.1, reserve: 1.15, reloadDuration: 0.9, projectileLife: 1.08, critChance: 1.18 },
  epic: { damage: 1.3, projectileSpeed: 1.18, spread: 0.8, cooldown: 0.87, magSize: 1.18, reserve: 1.24, reloadDuration: 0.84, projectileLife: 1.14, critChance: 1.3 },
  legendary: { damage: 1.45, projectileSpeed: 1.28, spread: 0.72, cooldown: 0.8, magSize: 1.28, reserve: 1.36, reloadDuration: 0.76, projectileLife: 1.2, critChance: 1.45 },
};

const CATEGORY_SCALING = {
  [WEAPON_CATEGORY.PISTOL]: {
    rare: { damage: 1.16, projectileSpeed: 1.12, spread: 0.82, cooldown: 0.9, critChance: 1.22 },
    legendary: { damage: 1.42, projectileSpeed: 1.3, spread: 0.66, cooldown: 0.76, critChance: 1.52 },
  },
  [WEAPON_CATEGORY.SHOTGUN]: {
    rare: { damage: 1.23, projectileSpeed: 1.08, spread: 0.92, reloadDuration: 0.88, reserve: 1.18 },
    legendary: { damage: 1.52, projectileSpeed: 1.2, spread: 0.86, reloadDuration: 0.74, reserve: 1.4 },
  },
  [WEAPON_CATEGORY.SMG]: {
    uncommon: { cooldown: 0.94, spread: 0.93, projectileSpeed: 1.05 },
    rare: { cooldown: 0.88, spread: 0.84, projectileSpeed: 1.12 },
    legendary: { cooldown: 0.73, spread: 0.7, projectileSpeed: 1.28 },
  },
  [WEAPON_CATEGORY.SNIPER]: {
    uncommon: { damage: 1.12, projectileSpeed: 1.08, spread: 0.82 },
    rare: { damage: 1.26, projectileSpeed: 1.18, spread: 0.68 },
    legendary: { damage: 1.58, projectileSpeed: 1.36, spread: 0.5 },
  },
  [WEAPON_CATEGORY.LAUNCHER]: {
    uncommon: { damage: 1.1, projectileSpeed: 1.03, projectileLife: 1.05, spread: 0.96 },
    rare: { damage: 1.2, projectileSpeed: 1.08, projectileLife: 1.12, spread: 0.9 },
    legendary: { damage: 1.48, projectileSpeed: 1.2, projectileLife: 1.24, spread: 0.8 },
  },
};

const weaponCache = new Map();

function mergeScaling(category, rarity) {
  const base = DEFAULT_SCALING[rarity] || DEFAULT_SCALING.common;
  const byCategory = CATEGORY_SCALING[category]?.[rarity] || null;
  if (!byCategory) return base;
  return { ...base, ...byCategory };
}

function clampRarity(rarity) {
  const safe = String(rarity || '').toLowerCase();
  if (RARITY_ORDER.includes(safe)) return safe;
  return 'common';
}

function scaleNumber(value, mul, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  if (!Number.isFinite(value)) return value;
  return Math.max(min, Math.min(max, value * mul));
}

function scaleInteger(value, mul, { min = 0 } = {}) {
  if (!Number.isFinite(value)) return value;
  return Math.max(min, Math.round(value * mul));
}

function scaleWeaponRuntime(runtime, scaling) {
  if (!runtime) return runtime;
  return {
    ...runtime,
    baseDamage: scaleNumber(runtime.baseDamage, scaling.damage, { min: 0.1 }),
    cooldown: scaleNumber(runtime.cooldown, scaling.cooldown, { min: 0.02 }),
    magSize: scaleInteger(runtime.magSize, scaling.magSize, { min: 1 }),
    reserve: scaleInteger(runtime.reserve, scaling.reserve, { min: 0 }),
    reloadDuration: scaleNumber(runtime.reloadDuration, scaling.reloadDuration, { min: 0.05 }),
    projectileSpeed: scaleNumber(runtime.projectileSpeed, scaling.projectileSpeed, { min: 0.5 }),
    projectileLife: scaleNumber(runtime.projectileLife, scaling.projectileLife, { min: 0.05 }),
    // Lower spread means tighter projectile grouping -> better effective accuracy.
    spread: scaleNumber(runtime.spread, scaling.spread, { min: 0 }),
    critChance: scaleNumber(runtime.critChance, scaling.critChance, { min: 0, max: 0.95 }),
  };
}

export function getWeaponById(weaponId, { rarityOverride = null } = {}) {
  const weapon = getBaseWeaponById(weaponId);
  if (!weapon) return null;

  const effectiveRarity = clampRarity(rarityOverride || weapon.rarity || 'common');
  const cacheKey = `${weapon.id}::${effectiveRarity}`;
  if (weaponCache.has(cacheKey)) return weaponCache.get(cacheKey);

  const scaling = mergeScaling(weapon.category, effectiveRarity);
  const resolved = {
    ...weapon,
    rarity: effectiveRarity,
    runtime: scaleWeaponRuntime(weapon.runtime, scaling),
  };
  weaponCache.set(cacheKey, resolved);
  return resolved;
}

export { pickRandomWeaponId, rarityColor, STARTER_WEAPON_IDS, WEAPON_CATEGORY, RARITY_ORDER };
